// 셔틀 관리 데이터 레이어. cmsContent와 달리 셔틀은 이미 완성된 스키마
// (adminContent/shuttleDraft|shuttlePublished, shuttleVersions/items/{version})가 있었다 —
// 레거시 인앱 관리자(src/components/admin/ShuttleDataAdmin.jsx)와 실제 앱의
// loadPublishedShuttleData()가 이미 이 정확한 문서 모양을 쓰고 있으므로, 새 스키마를 만드는 대신
// src/services/shuttleCmsService.js·src/utils/validateShuttleData.js를 그대로 재사용해 두 관리자
// UI가 항상 같은 정규화/검증 로직을 공유하게 한다(로직이 두 곳에서 갈라지는 것을 방지).
import { collection, doc, getDoc, getDocs, limit, orderBy, query, serverTimestamp, writeBatch } from 'firebase/firestore'
import { addAudit, formatDateTime, requireDb, toDate } from './data'
import {
  buildLocalShuttleCmsData,
  normalizeShuttleCmsData,
  SHUTTLE_DRAFT_DOC,
  SHUTTLE_PUBLISHED_DOC,
  SHUTTLE_VERSION_COLLECTION,
} from '../../../../src/services/shuttleCmsService'
import { validateShuttleData } from '../../../../src/utils/validateShuttleData'

function nowVersionId() {
  const d = new Date()
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`
}

function itemCounts(data) {
  return { stops: data.stops?.length ?? 0, routes: data.routes?.length ?? 0, schedules: data.schedules?.length ?? 0 }
}

export async function fetchShuttleContent() {
  const db = requireDb()
  const [draftSnap, publishedSnap] = await Promise.all([
    getDoc(doc(db, ...SHUTTLE_DRAFT_DOC)),
    getDoc(doc(db, ...SHUTTLE_PUBLISHED_DOC)),
  ])
  return {
    draft: draftSnap.exists() ? draftSnap.data()?.main ?? null : null,
    production: publishedSnap.exists() ? publishedSnap.data()?.main ?? null : null,
  }
}

export async function fetchShuttleVersions(max = 20) {
  const db = requireDb()
  const snapshot = await getDocs(query(collection(db, ...SHUTTLE_VERSION_COLLECTION), orderBy('createdAt', 'desc'), limit(max)))
  return snapshot.docs.map(item => item.data())
}

export async function saveShuttleDraft(data, { uid }) {
  const db = requireDb()
  const normalized = normalizeShuttleCmsData(data)
  const batch = writeBatch(db)
  batch.set(doc(db, ...SHUTTLE_DRAFT_DOC), { main: { ...normalized, status: 'draft', updatedAt: serverTimestamp(), updatedBy: uid } }, { merge: true })
  addAudit(batch, { action: 'draft-save', entityType: 'cms-shuttle', entityId: 'shuttle', actorUid: uid, before: null, after: { contentType: 'shuttle', itemCount: Object.values(itemCounts(normalized)).reduce((a, b) => a + b, 0) } })
  await batch.commit()
}

export async function discardShuttleDraft(uid) {
  const db = requireDb()
  const batch = writeBatch(db)
  batch.delete(doc(db, ...SHUTTLE_DRAFT_DOC))
  addAudit(batch, { action: 'delete', entityType: 'cms-shuttle', entityId: 'shuttle', actorUid: uid, before: null, after: null })
  await batch.commit()
}

// 검증 실패 시 배포하지 않고 던진다 — 호출부 UI가 이미 같은 검증 결과를 보여줬어야 하므로
// 여기서 막히는 것은 우회 시도이거나 방어적 이중 확인이다(다른 CMS 문서와 동일한 계약).
export async function publishShuttleData(data, { uid }) {
  const normalized = normalizeShuttleCmsData(data)
  const validation = validateShuttleData(normalized)
  if (!validation.ok) {
    const error = new Error('치명적 오류가 있어 배포할 수 없습니다.')
    error.validation = validation
    throw error
  }
  const db = requireDb()
  const version = nowVersionId()
  const published = { ...normalized, publishedAt: serverTimestamp(), publishedBy: uid, version }
  const batch = writeBatch(db)
  batch.set(doc(db, ...SHUTTLE_PUBLISHED_DOC), { main: published }, { merge: true })
  batch.set(doc(collection(db, ...SHUTTLE_VERSION_COLLECTION), version), { version, data: normalized, createdAt: serverTimestamp(), createdBy: uid })
  batch.delete(doc(db, ...SHUTTLE_DRAFT_DOC))
  addAudit(batch, { action: 'publish', entityType: 'cms-shuttle', entityId: 'shuttle', actorUid: uid, before: null, after: { contentType: 'shuttle', version } })
  await batch.commit()
  return version
}

export async function rollbackShuttleData(versionDoc, { uid }) {
  const restored = normalizeShuttleCmsData(versionDoc.data)
  const validation = validateShuttleData(restored)
  if (!validation.ok) {
    const error = new Error('과거 버전 데이터가 현재 검증 규칙을 통과하지 못해 롤백할 수 없습니다.')
    error.validation = validation
    throw error
  }
  const db = requireDb()
  const version = `rollback-${versionDoc.version}`
  const published = { ...restored, publishedAt: serverTimestamp(), publishedBy: uid, version }
  const batch = writeBatch(db)
  batch.set(doc(db, ...SHUTTLE_PUBLISHED_DOC), { main: published }, { merge: true })
  addAudit(batch, { action: 'rollback', entityType: 'cms-shuttle', entityId: 'shuttle', actorUid: uid, before: { version: versionDoc.version }, after: { contentType: 'shuttle', version } })
  await batch.commit()
  return version
}

export { buildLocalShuttleCmsData, formatDateTime, normalizeShuttleCmsData, toDate, validateShuttleData }
